import './styles/main.css';
import './styles/ui.css';
import './styles/menu.css';
import './styles/quiz.css';
import './styles/minigames.css';
import './styles/debug.css';
import { CHARACTERS, CharacterCatalog } from './game/characters/CharacterCatalog';
import { findCharacterId } from './game/characters/characterSelection';
import { GAME_CONFIG, type GameConfig } from './game/config';
import { CHARACTER_CONFIG } from './game/config/character.config';
import type { RenderQuality } from './game/config/rendering.config';
import { Game } from './game/core/Game';
import { DifficultyManager } from './game/difficulty/DifficultyManager';
import { DOZER_SET_PIECE } from './game/level/segments/definitions/SEG_DOZER_60M';
import { GOLDEN_RAMP } from './game/level/segments/definitions/SEG_GOLDEN_RAMP_60M';
import { DEFAULT_SEGMENTS } from './game/level/segments/SegmentCatalog';
import type { SegmentGenerationRules } from './game/level/segments/SegmentDefinition';
import { detectQuality, lowerQuality } from './game/rendering/qualitySelection';
import { initAnalytics } from './game/services/analytics';
import { LightingZone } from './game/zones/ZoneSchedule';

/** Remembers a lighter rendering profile after a device lost its WebGL context. */
const QUALITY_FALLBACK_KEY = 'bau-run.quality-fallback';
const GPU_FAILURE = /vertex buffer|index buffer|context|webgl/i;

function readSeed(): number | undefined {
  const fromUrl = new URLSearchParams(window.location.search).get('seed');
  const raw = fromUrl ?? import.meta.env.VITE_RNG_SEED;
  const seed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isFinite(seed) ? seed >>> 0 : undefined;
}

function readStoredFallback(): string | null {
  try {
    return window.localStorage.getItem(QUALITY_FALLBACK_KEY);
  } catch {
    return null;
  }
}

/** Reloads with the next lighter profile. Returns false if there is none left. */
function retryLighter(current: RenderQuality): boolean {
  const next = lowerQuality(current);
  if (!next) return false;
  try {
    window.localStorage.setItem(QUALITY_FALLBACK_KEY, next);
  } catch {
    return false; // without storage the reload would pick the same profile again
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('quality'); // an explicit ?quality= would override the fallback
  window.location.replace(url.toString());
  return true;
}

function gpuName(): string {
  const gl = document.createElement('canvas').getContext('webgl2') ?? document.createElement('canvas').getContext('webgl');
  if (!gl) return 'kein WebGL';
  const info = gl.getExtension('WEBGL_debug_renderer_info');
  return String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
}

function showFatal(text: string): void {
  const message = document.createElement('pre');
  message.style.cssText = 'position:fixed;inset:16px;color:#fff;white-space:pre-wrap;font:14px monospace;z-index:10;';
  message.textContent = text;
  document.body.appendChild(message);
}

/** Test overrides for ?segment=: matching modules appear from 40 m on with a high weight (still never twice in a row). */
function segmentTestOverrides(fragment: string | null): Record<string, Partial<SegmentGenerationRules>> {
  if (!fragment) return {};
  const needle = fragment.toUpperCase();
  const matching = DEFAULT_SEGMENTS.filter((s) => !s.junction && s.id.includes(needle));
  // Early, but never later than the module's own rule – the start module (0 m) must stay available behind the start.
  return Object.fromEntries(
    matching.map((s) => [s.id, { minDistance: Math.min(s.generation.minDistance, 40), weight: 20 / matching.length, cooldownDistance: 0, minSegmentsAfterJunction: 0 }]),
  );
}

/** ?zone=night|day → fixed lighting zone. */
function zoneParam(value: string | null): LightingZone | undefined {
  const zone = value?.toUpperCase();
  return zone === LightingZone.Night || zone === LightingZone.Day ? zone : undefined;
}

/**
 * ?segment= values of the scheduled set pieces and their first mark (run distance): the test brings the schedule
 * forward, the run starts at the speed it would have at that mark.
 */
const SET_PIECE_TESTS: Readonly<Record<string, number>> = {
  golden: GOLDEN_RAMP.placement.stretches[0].from,
  dozer: DOZER_SET_PIECE.placement.stretches[0].from,
};

/**
 * ?speed=<m/s> → start speed of the run (testing aid). ?segment=golden / ?segment=dozer default to the speed at the
 * set piece's first mark (golden 500 m, dozer 600 m): the test brings it to ~40–100 m, where the run would still be
 * slower than at that mark (the dozer is mounted with the speed it is really met with).
 */
function startSpeedParam(params: URLSearchParams): number | undefined {
  const fromUrl = Number.parseFloat(params.get('speed') ?? '');
  if (Number.isFinite(fromUrl) && fromUrl > 0) return fromUrl;
  const firstMark = SET_PIECE_TESTS[params.get('segment')?.toLowerCase() ?? ''];
  return firstMark === undefined ? undefined : new DifficultyManager(GAME_CONFIG.difficulty).speedAt(firstMark);
}

/** The game config with the run starting at `start` m/s (the difficulty ramp goes on from there, up to max). */
function withStartSpeed(config: GameConfig, start: number): GameConfig {
  const { speed } = config.difficulty;
  return { ...config, difficulty: { ...config.difficulty, speed: { ...speed, start: Math.min(start, speed.max) } } };
}

const quality = detectQuality(window.location.search, window.matchMedia('(pointer: coarse)').matches, readStoredFallback());

async function bootstrap(): Promise<void> {
  // Google Analytics only after consent (consent bar), only on bauralley.com
  initAnalytics();
  const canvas = document.getElementById('render-canvas');
  const uiRoot = document.getElementById('ui-root');
  if (!(canvas instanceof HTMLCanvasElement) || !uiRoot) throw new Error('index.html is missing #render-canvas or #ui-root');

  const seed = readSeed();
  const params = new URLSearchParams(window.location.search);
  const debugParam = params.get('debug');
  // Testing aid: ?collapse=early brings the collapsing building to ~150 m (normally from 2 km).
  // ?segment=<id fragment> (e.g. ramp, pit_small, scaffold, pipes; moving hazards: crane, excavator, forklift, truck;
  // golden: the golden bottle ramps from ~100 m on – the whole schedule 500/1000/…/2000/3000/… m moves forward by
  // 460 m – at the speed of the first mark, see startSpeedParam; dozer: the dozer at ~40 m, then every 600 m, at the
  // speed of its first mark)
  // makes those modules appear early and often.
  const generationOverrides = {
    ...(params.get('collapse') === 'early' ? { SEG_COLLAPSE_40M: { minDistance: 150, cooldownDistance: 500 } } : {}),
    ...segmentTestOverrides(params.get('segment')),
  };
  // Testing aid: ?zone=night / ?zone=day fixes the lighting zone (day/night schedule otherwise).
  const zoneOverride = zoneParam(params.get('zone'));
  const startSpeed = startSpeedParam(params);
  // Testing aid: ?character=electrician|carpenter (or the character id) preselects a character.
  const character = findCharacterId(params.get('character'), new CharacterCatalog(CHARACTERS), CHARACTER_CONFIG);
  const game = new Game({
    canvas,
    uiRoot,
    ...(seed !== undefined ? { seed } : {}),
    debugAvailable: import.meta.env.DEV || debugParam !== null,
    quality,
    ...(startSpeed !== undefined ? { config: withStartSpeed(GAME_CONFIG, startSpeed) } : {}),
    ...(Object.keys(generationOverrides).length > 0 ? { generationOverrides } : {}),
    ...(zoneOverride ? { zoneOverride } : {}),
    ...(character ? { character } : {}),
    ...(debugParam !== null ? { debugStartEnabled: debugParam !== '0' } : {}),
  });
  console.info(`[BAU RUN] rendering quality: ${quality}`);
  // A phone GPU running out of memory loses the context – retry with a lighter profile.
  game.engine.onContextLostObservable.add(() => {
    if (!retryLighter(quality)) showFatal(`Die Grafik dieses Geräts ist ausgefallen (Qualität: ${quality}, GPU: ${gpuName()}).`);
  });
  if (import.meta.env.DEV) {
    const { installDevHooks } = await import('./game/debug/DevHooks');
    installDevHooks(game);
  }
  await game.init();
  canvas.focus();
}

bootstrap().catch((error: unknown) => {
  console.error('[BAU RUN] startup failed', error);
  const text = error instanceof Error ? error.message : String(error);
  if (GPU_FAILURE.test(text) && retryLighter(quality)) return;
  showFatal(`BAU RALLEY konnte nicht starten:\n${text}\n\nQualität: ${quality} · GPU: ${gpuName()}`);
});
